import { apiAuth, apiResponseType } from ".";

/** Banco de Bolões / Banco de Jogos (LOT-62) */

export interface GameBankRules {
  min: number;
  max: number;
  /** Loterias posicionais (Super Sete): a ordem dos números importa */
  ordered: boolean;
  /** Quantidade fixa de números por jogo (Super Sete = 7) */
  length: number | null;
}

export interface GameBankGroup {
  dezenas: number;
  games_count: number;
}

export interface GameBankLottery {
  id: number;
  name: string;
  color: string | null;
  rules: GameBankRules;
  groups: GameBankGroup[];
  total_games: number;
  /** Planilhas em andamento (jogos ainda fora do banco) */
  open_sheets: number;
}

export interface GameBankGame {
  id: number;
  uuid: string;
  game_number: number;
  dezenas: number;
  numbers: number[];
  created_at: string;
}

export interface GameBankGamesPaginated {
  data: GameBankGame[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from: number | null;
  to: number | null;
}

/** ok = verde (3+ diferentes), attention = amarelo (2), high = laranja (1), repeated = vermelho (0) */
export type GameCheckStatus = "ok" | "attention" | "high" | "repeated" | "invalid";

/** Resultado da conferência de uma linha da planilha */
export interface GameBankSheetAnalysisRow {
  /** Linha da planilha (0 a N-1) */
  line: number;
  status: GameCheckStatus;
  errors: string[];
  equal_count: number;
  difference: number | null;
  match: {
    /** bank = jogo já cadastrado; sheet = outra linha da própria planilha */
    source: "bank" | "sheet";
    game_id: number | null;
    game_number: number | null;
    dezenas: number;
    /** Linha da planilha (1 a N) quando source = sheet */
    row: number | null;
  } | null;
}

/** Status da planilha: Em andamento (editável) → Finalizado (jogos no banco); Arquivado = fora da listagem */
export type GameBankSheetStatus = "in_progress" | "finished" | "archived";

/** Status da conferência. "analyzing" fica reservado para a conferência em segundo plano (ainda não usado) */
export type GameBankSheetCheckStatus = "pending" | "analyzing" | "analyzed";

interface UserRef {
  id: number;
  name: string;
}

export interface GameBankSheetSummary {
  id: number;
  uuid: string;
  number: number;
  lottery_id: number;
  dezenas: number;
  status: GameBankSheetStatus;
  check_status: GameBankSheetCheckStatus;
  /** Jogos (linhas preenchidas) */
  rows_count: number;
  /** Trava do autosave: enviada em cada gravação */
  version: number;
  summary: { ok: number; attention: number; repeated: number; invalid: number };
  /** Em andamento, conferida, sem repetido nem inválido */
  can_finish: boolean;
  analyzed_at: string | null;
  /** Quando a conferência voltou para Pendente porque o banco mudou */
  invalidated_at: string | null;
  invalidated_by_sheet_number: number | null;
  /** Quando os jogos entraram no banco (continua preenchido mesmo depois de arquivar) */
  saved_at: string | null;
  archived_at: string | null;
  created_by: UserRef | null;
  updated_by: UserRef | null;
  created_at: string;
  updated_at: string;
}

export interface GameBankSheet extends GameBankSheetSummary {
  lottery: { id: number; name: string; color: string | null } | null;
  saved_by: UserRef | null;
  archived_by: UserRef | null;
  rows: string[][];
  analysis: { rows: GameBankSheetAnalysisRow[] } | null;
}

export interface GameBankSheetsPaginated {
  data: GameBankSheetSummary[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

export const getGameBankLotteries = (): apiResponseType =>
  apiAuth.get("game-bank/lotteries");

export const getGameBankGames = (params: {
  lottery_id: number;
  dezenas: number;
  page?: number;
  per_page?: number;
  search?: string;
}): apiResponseType => {
  const query = new URLSearchParams();
  query.append("lottery_id", String(params.lottery_id));
  query.append("dezenas", String(params.dezenas));
  if (params.page) query.append("page", String(params.page));
  if (params.per_page) query.append("per_page", String(params.per_page));
  if (params.search) query.append("search", params.search);

  return apiAuth.get(`game-bank/games?${query.toString()}`);
};

export const getGameBankSheets = (params: {
  lottery_id: number;
  /** Sem status: em andamento e finalizadas (arquivadas só com status = archived) */
  status?: GameBankSheetStatus;
  dezenas?: number;
  page?: number;
  per_page?: number;
}): apiResponseType => {
  const query = new URLSearchParams();
  query.append("lottery_id", String(params.lottery_id));
  if (params.status) query.append("status", params.status);
  if (params.dezenas) query.append("dezenas", String(params.dezenas));
  if (params.page) query.append("page", String(params.page));
  if (params.per_page) query.append("per_page", String(params.per_page));

  return apiAuth.get(`game-bank/sheets?${query.toString()}`);
};

export const getGameBankSheet = (uuid: string): apiResponseType => apiAuth.get(`game-bank/sheets/${uuid}`);

/** Primeiro autosave: cria a planilha Em andamento, conferência Pendente */
export const createGameBankSheet = (payload: {
  lottery_id: number;
  dezenas: number;
  rows: string[][];
}): apiResponseType => apiAuth.post("game-bank/sheets", payload);

/** Autosave. 409 quando outra pessoa gravou antes (version antiga) */
export const updateGameBankSheet = (uuid: string, payload: { rows: string[][]; version: number }): apiResponseType =>
  apiAuth.put(`game-bank/sheets/${uuid}`, payload);

/** Grava as células e confere contra o banco inteiro da loteria */
export const analyzeGameBankSheet = (uuid: string, payload: { rows: string[][]; version: number }): apiResponseType =>
  apiAuth.post(`game-bank/sheets/${uuid}/analyze`, payload);

/** Finaliza: os jogos entram no banco. 409 quando o banco mudou depois da conferência (volta para Pendente) */
export const finishGameBankSheet = (uuid: string): apiResponseType => apiAuth.post(`game-bank/sheets/${uuid}/finish`);

/** Tira a planilha da listagem (em andamento: descontinua; finalizada: os jogos continuam no banco) */
export const archiveGameBankSheet = (uuid: string): apiResponseType => apiAuth.post(`game-bank/sheets/${uuid}/archive`);

/** Volta para a listagem: Finalizado se já foi para o banco, senão Em andamento com a conferência Pendente */
export const unarchiveGameBankSheet = (uuid: string): apiResponseType =>
  apiAuth.post(`game-bank/sheets/${uuid}/unarchive`);

export type ExportFormat = "xlsx" | "csv";

/** Todos os jogos de um grupo (loteria + quantidade de dezenas) em Excel ou CSV */
export const exportGameBankGames = (params: { lottery_id: number; dezenas: number }, format: ExportFormat) => {
  const query = new URLSearchParams();
  query.append("lottery_id", String(params.lottery_id));
  query.append("dezenas", String(params.dezenas));
  query.append("format", format);

  return apiAuth.get(`game-bank/games/export?${query.toString()}`, { responseType: "blob" }) as apiResponseType;
};

/** A planilha em Excel ou CSV, com o resultado da conferência e, se finalizada, o Nº de cada jogo no banco */
export const exportGameBankSheet = (uuid: string, format: ExportFormat) =>
  apiAuth.get(`game-bank/sheets/${uuid}/export?format=${format}`, { responseType: "blob" }) as apiResponseType;

type FailedCall = { response?: { status?: number; data?: { message?: string; response?: GameBankSheet | null } } };

/** Status HTTP de uma chamada que falhou (o interceptor devolve o erro do axios com ok = false) */
export const failedStatus = (result: unknown): number | undefined => (result as FailedCall)?.response?.status;

/** Planilha atual devolvida junto com um 409 */
export const failedSheet = (result: unknown): GameBankSheet | null =>
  (result as FailedCall)?.response?.data?.response ?? null;

export const failedMessage = (result: unknown): string | undefined =>
  (result as FailedCall)?.response?.data?.message;
